import { ColumnType, NameViewMode, Show } from "../../../../constants/schema.js";
import { getLiveDomainName } from "../../dictionary/membership.js";
import { bHas } from "../../../../utils/bit.js";
import { FocusType, ViewKind } from "../state.js";
//#region src/engine/modules/editor/utils/nameView.ts
var sceneNameSettings = (settings, source = "document") => source === "document" ? settings : {
	nameViewMode: settings.nameViewMode,
	show: 0
};
var isLogicalNameView = ({ nameViewMode }) => nameViewMode === NameViewMode.logical;
var isBothNameView = ({ nameViewMode }) => nameViewMode === NameViewMode.both;
var drawsNameForLogicalName = (settings, { logicalName }) => isLogicalNameView(settings) && !logicalName.trim();
function toNameLabel(settings, { name, logicalName }) {
	const logical = logicalName.trim() ? logicalName : "";
	if (isLogicalNameView(settings)) return logical ? {
		text: logical,
		after: "",
		standIn: false
	} : {
		text: name,
		after: "",
		standIn: name.trim() !== ""
	};
	if (isBothNameView(settings) && logical) return {
		text: logical,
		after: name.trim() ? name : "",
		standIn: false
	};
	return {
		text: name,
		after: "",
		standIn: false
	};
}
var nameLabelText = ({ text, after }) => after ? `${text} (${after})` : text;
var isDomainInTypeCell = (settings) => isLogicalNameView(settings) && !bHas(settings.show, Show.columnDomain);
var drawsDomainInTypeCell = (state, column, source = "document") => isDomainInTypeCell(sceneNameSettings(state.settings, source)) && getLiveDomainName(state, column.domainId) !== "";
function getTableFocusTypes(settings) {
	const names = isLogicalNameView(settings) ? [FocusType.tableLogicalName] : isBothNameView(settings) ? [FocusType.tableLogicalName, FocusType.tableName] : [FocusType.tableName];
	return bHas(settings.show, Show.tableComment) ? [...names, FocusType.tableComment] : names;
}
var shownBy = (bit) => ({ show }) => bHas(show, bit);
var ColumnTypeShown = {
	[ColumnType.columnName]: () => true,
	[ColumnType.columnLogicalName]: isBothNameView,
	[ColumnType.columnDataType]: shownBy(Show.columnDataType),
	[ColumnType.columnDomain]: shownBy(Show.columnDomain),
	[ColumnType.columnNotNull]: shownBy(Show.columnNotNull),
	[ColumnType.columnUnique]: shownBy(Show.columnUnique),
	[ColumnType.columnAutoIncrement]: shownBy(Show.columnAutoIncrement),
	[ColumnType.columnDefault]: shownBy(Show.columnDefault),
	[ColumnType.columnComment]: shownBy(Show.columnComment)
};
var COLUMN_FOCUS_TYPES = {
	[ColumnType.columnName]: FocusType.columnName,
	[ColumnType.columnLogicalName]: FocusType.columnLogicalName,
	[ColumnType.columnDataType]: FocusType.columnDataType,
	[ColumnType.columnDomain]: FocusType.columnDomain,
	[ColumnType.columnNotNull]: FocusType.columnNotNull,
	[ColumnType.columnUnique]: FocusType.columnUnique,
	[ColumnType.columnAutoIncrement]: FocusType.columnAutoIncrement,
	[ColumnType.columnDefault]: FocusType.columnDefault,
	[ColumnType.columnComment]: FocusType.columnComment
};
function toNameViewFocusType(settings, focusType) {
	if (isLogicalNameView(settings)) {
		if (focusType === FocusType.tableName) return FocusType.tableLogicalName;
		if (focusType === FocusType.columnName) return FocusType.columnLogicalName;
	}
	if (focusType === FocusType.columnDataType && isDomainInTypeCell(settings)) return FocusType.columnDomain;
	return focusType;
}
function getColumnFocusTypes(settings) {
	const types = [];
	for (const columnType of settings.columnOrder) {
		const focusType = COLUMN_FOCUS_TYPES[columnType];
		if (focusType && ColumnTypeShown[columnType](settings)) types.push(toNameViewFocusType(settings, focusType));
	}
	return types;
}
function getViewColumnTypes(settings) {
	return isBothNameView(settings) ? [
		ColumnType.columnLogicalName,
		ColumnType.columnName,
		ColumnType.columnDomain,
		ColumnType.columnDataType
	] : [ColumnType.columnName, ColumnType.columnDataType];
}
function getViewColumnFocusTypes(settings) {
	const shown = sceneNameSettings(settings, ViewKind.flow);
	return getViewColumnTypes(settings).map((columnType) => toNameViewFocusType(shown, COLUMN_FOCUS_TYPES[columnType]));
}
var PHYSICAL_FOCUS_TYPES = {
	[FocusType.tableLogicalName]: FocusType.tableName,
	[FocusType.columnLogicalName]: FocusType.columnName,
	[FocusType.columnDomain]: FocusType.columnDataType
};
function toShownFocusType(settings, types, focusType) {
	if (types.includes(focusType)) return focusType;
	const shown = toNameViewFocusType(settings, PHYSICAL_FOCUS_TYPES[focusType] ?? focusType);
	return types.includes(shown) ? shown : null;
}
function keepFocusShown(state) {
	const { focusTable } = state.editor;
	if (!focusTable) return;
	const types = focusTable.columnId ? getColumnFocusTypes(state.settings) : getTableFocusTypes(state.settings);
	const shown = toShownFocusType(state.settings, types, focusTable.focusType);
	if (!shown || shown === focusTable.focusType) return;
	focusTable.focusType = shown;
	focusTable.edit = false;
}
//#endregion
export { ColumnTypeShown, drawsDomainInTypeCell, drawsNameForLogicalName, getColumnFocusTypes, getTableFocusTypes, getViewColumnFocusTypes, getViewColumnTypes, isBothNameView, isDomainInTypeCell, isLogicalNameView, keepFocusShown, nameLabelText, sceneNameSettings, toNameLabel, toNameViewFocusType, toShownFocusType };
